import { FormProvider, useForm } from "react-hook-form";
import { getFullPath, routes } from "../../routes";
import { InformationDialog } from "@/components/dialogs";
import { SavePrompt } from "@/components";
import { SpinButton } from "@/components/form/SpinButton";
import { TEForm } from "./TEForm";
import { TEFormAdvisories } from "./TEFormAdvisories";
import { TEFormBreadcrumbs } from "./TEFormBreadcrumbs";
import { TEFormToolbar } from "./TEFormToolbar";
import { TEFormWelders } from "./TEFormWelders";
import { TestEvent, TestEventStatus } from "@/api/williams/testEvents";
import { TestEventSchema } from "../TestEventSchema";
import { TestInput } from "../components";
import { useAdvisories, useSaveTestEvent } from "../hooks";
import { useModalDialog } from "@/hooks";
import { useNavigate } from "react-router";
import { zodResolver } from "@hookform/resolvers/zod";

export type TestEventFormProps = {
  data: TestEvent;
};

export function TestEventForm({ data: initialData }: Readonly<TestEventFormProps>) {
  const save = useSaveTestEvent();
  const navigate = useNavigate();

  const form = useForm<TestEvent>({
    defaultValues: initialData,
    resolver: zodResolver(TestEventSchema),
    reValidateMode: "onChange",
  });

  const data = form.watch();
  const errors = form.formState.errors;
  const isDirty = Object.keys(form.formState.dirtyFields).length > 0;
  const advisories = useAdvisories(data);
  const modal = useModalDialog();

  function checkAdvisories() {
    if (advisories.data?.length !== 0) {
      modal.show(InformationDialog, {
        caption: "Check Advisories",
        message: "Invalid selection. Please check advisories.",
      });
    }
    return advisories.data?.length === 0;
  }

  function handleViewTestResults(data: TestEvent) {
    if (checkAdvisories()) {
      save.mutateAsync(data, {
        onSuccess: () => {
          navigate(getFullPath(routes.testEvent.results(data.id)), {
            state: {
              skipSavePrompt: true,
            },
          });
        },
      });
    }
  }

  return (
    <SavePrompt when={isDirty}>
      <div className="mb-5" style={{ maxWidth: 1000 }}>
        <TEFormBreadcrumbs data={data} />

        <FormProvider {...form}>
          <div className="d-md-flex justify-content-between mb-2">
            <h3>Test Event</h3>
            <TEFormToolbar data={data} />
          </div>

          <TEForm data={data} />

          <div>
            <label className="col-form-label pb-0">Standard Tests</label>
            <TestInput data={data} />
            <div>{errors.tests && <span className="text-danger">{errors.tests.message}</span>}</div>
          </div>
          <div className="mt-4">
            <TEFormWelders data={data} />
            <TEFormAdvisories data={data} />
          </div>

          {data.welders.length > 0 && data.tests.length > 0 && (
            <SpinButton
              className="btn btn-default mt-2"
              onClick={form.handleSubmit(handleViewTestResults)}
              disabled={data.welderTests.length === 0 || data.status === TestEventStatus.Cancelled}
              loading={save.isLoading}
            >
              Enter Test Results
            </SpinButton>
          )}
        </FormProvider>
      </div>
    </SavePrompt>
  );
}
